import {Component, EventEmitter, Input, Output, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {Subject, takeUntil} from "rxjs";
import {CreditSafeService} from "./credit-safe.service";
import {CommonModule} from "@angular/common";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-credit-safe-credit-checker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
  ],
  templateUrl: './credit-safe-credit-checker.component.html',
  styleUrl: './credit-safe-credit-checker.component.scss'
})

export class CreditSafeCreditCheckerComponent implements OnInit {
  @Input() templateKey: string;
  @Input() isEFormValid: boolean;
  @Input() personalNumber?: string;
  @Input() organizationNumber?: string;
  @Input() isSignatorySelected: boolean = false;
  @Input() eForm: any;

  @Output() creditRatingApproved = new EventEmitter<any>();
  @Output() creditRatingRejected = new EventEmitter<void>();
  @Output() creditCheckRequestFailed = new EventEmitter<void>();
  @Output() validateEForm = new EventEmitter<void>();

  checkCreditForm: FormGroup;

  isLoading = false;
  creditCheckRejected = false;
  creditCheckFailRequest = false;

  private destroy$ = new Subject<void>();

  constructor(
      private fb: FormBuilder,
      public creditSafeService: CreditSafeService,
      private sanitizer: DomSanitizer
  ) {
  }

  ngOnInit() {
    // Initialize form here when inputs are available
    this.checkCreditForm = this.fb.group({
      templateKey: [this.templateKey, [Validators.required]],
      agreeForCreditCheck: [false, [Validators.requiredTrue]],
      numberToCheck: [this.organizationNumber || this.personalNumber || '', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      isSignatorySelected: [this.isSignatorySelected || false, [Validators.requiredTrue]],
    });
  }

  onAgreeToggled() {
    if (this.checkCreditForm) {
      this.updateFormValues();
    }
  }

  private updateFormValues() {
    this.checkCreditForm.patchValue({
      templateKey: this.templateKey,
      numberToCheck: this.organizationNumber || this.personalNumber || '',
      isSignatorySelected: this.isSignatorySelected || false,
    });
  }

  checkCreditRating() {
    if (this.checkCreditForm.invalid) {
      return;
    }

    this.validateEForm.emit();

    if (!this.isEFormValid) {
      return;
    }

    if (this.organizationNumber) {
      this.checkOrganizationCreditRating(this.organizationNumber);
    } else {
      this.checkPersonCreditRating(this.personalNumber);
    }
  }

  checkOrganizationCreditRating(organizationNumber: string): void {
    this.isLoading = true;

    this.creditSafeService.checkOrganizationCreditRating(
      this.templateKey,
      organizationNumber,
      this.getEFormFieldValue('email', 'personfields'),
      this.getEFormFieldValue('phone_no', 'orgfields') || this.getEFormFieldValue('phone_no', 'personfields')
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isLoading = false;

        if (response.status === 'success') {
          if (response.status_list.status_txt === 'Approved') {
            this.creditCheckApprovedHandler(response);
          } else {
            this.creditCheckRejectedHandler();
          }
        } else {
          this.creditCheckValidationHandler(response);
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.creditCheckValidationHandler(error);
      }
    });
  }

  checkPersonCreditRating(personalNumber: string): void {
    this.isLoading = true;

    this.creditSafeService.checkPersonCreditRating(
      this.templateKey,
      personalNumber,
      this.getEFormFieldValue('email', 'personfields'),
      this.getEFormFieldValue('phone_no', 'personfields') || this.getEFormFieldValue('phone_no', 'orgfields'),
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isLoading = false;

        if (response.status === 'success') {
          if (response.status_list.status_txt === 'Approved') {
            this.creditCheckApprovedHandler(response);
          } else {
            this.creditCheckRejectedHandler();
          }
        } else {
          this.creditCheckValidationHandler(response);
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.creditCheckValidationHandler(error);
      }
    });
  }

  creditCheckApprovedHandler(creditSafeData: any) {
    this.creditCheckRejected = false;
    this.creditCheckFailRequest = false;
    this.creditRatingApproved.emit(creditSafeData);
  }

  creditCheckRejectedHandler() {
    this.creditCheckRejected = true;
    this.creditCheckFailRequest = false;
    this.creditRatingRejected.emit();
  }

  creditCheckValidationHandler(error: any) {
    this.creditCheckRejected = false;
    this.creditCheckFailRequest = true;
    this.creditCheckRequestFailed.emit();
  }

  getEFormFieldValue(fieldKey: string, fieldType: string, roleIndex: number = 0): string {
    const role = this.eForm.get(`template_roles.${roleIndex}`);
    const formFields = role?.get(fieldType) as FormArray;
    if (formFields) {
      for (let i = 0; i < formFields.length; i++) {
        const field = formFields.at(i);
        if (field.get('key')?.value === fieldKey) {
          return field.get('value')?.value;
        }
      }
    }

    return '';
  }

  getRejectMessageWithEmailLinks(): SafeHtml {
    const message = this.creditSafeService.getSkoogsRejectMessage(this.templateKey);
    if (!message) return '';
    
    const messageWithLinks = this.convertEmailsToLinks(message);
    return this.sanitizer.bypassSecurityTrustHtml(messageWithLinks);
  }

  private convertEmailsToLinks(text: string): string {
    // Regular expression to match email addresses
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    
    return text.replace(emailRegex, '<a href="mailto:$1" target="_blank">$1</a>');
  }

}